import { useEffect, useState } from "react";
import clsx from "clsx";

// MUI
import { Card, Divider, Typography, Box, CircularProgress, IconButton, CardContent } from "@mui/material";
import RateReviewIcon from "@mui/icons-material/RateReview";
import EqualizerRoundedIcon from "@mui/icons-material/EqualizerRounded";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";

// Components
import SwipeableViews from "react-swipeable-views-react-18-fix";

/* Interfaces */
interface Card {
	id: number;
	title: string;
	description: string;
	icon: React.ReactNode;
	date: number;
}

/* Constants */
const MAX_DOTS = 5;
const SIZE_SPINNER = 10;
const INCREMENT_PROGRESS = 5;
const COLOR_ICON = "var(--medium-pink-color)";
const SECONDS_COMPLETE_SPINNER = 1;
const INTERVAL_TIME = (SECONDS_COMPLETE_SPINNER * 1000) / INCREMENT_PROGRESS;

const cards: Card[] = [
	{
		id: 1,
		title: "Social Media Reviews",
		description: `When inviting customers to leave a review, using the Podium or Reputation.com Apps, 
			there are certain suggested steps in order to get the best out of these tools. Click here in order to read more.`,
		icon: <RateReviewIcon sx={{ color: COLOR_ICON, fontSize: "2rem" }} />,
		date: Date.now(),
	},
	{
		id: 2,
		title: "Improving Your CSI Score",
		description: `Your CSI is an important metric used to measure customer satisfaction during the sales 
			and service process. This document contains helpful tips that will guide you on how to improve your score.`,
		icon: <EqualizerRoundedIcon sx={{ color: COLOR_ICON, fontSize: "2rem" }} />,
		date: Date.now(),
	},
];
const TipsAndTricks = () => {
	const [activeStep, setActiveStep] = useState<number>(0);
	const [progress, setProgress] = useState<number>(0);
	const [automatically, setAutomatically] = useState<boolean>(true);
	const [intervalNumber, setIntervalNumber] = useState<number>(0);
	const maxSteps = cards.length;

	useEffect(() => {
		const intervalId = setInterval(() => {
			setProgress((prevProgress) => {
				if (prevProgress >= 100) {
					if (automatically) {
						handleNext();
					}
					return 0;
				} else if (prevProgress + INCREMENT_PROGRESS >= 100) {
					return 100;
				} else {
					return prevProgress + INCREMENT_PROGRESS;
				}
			});
		}, INTERVAL_TIME);

		setIntervalNumber(Number(intervalId));

		return () => {
			clearInterval(intervalId);
		};
	}, []);

	const handleNext = (stop = false) => {
		if (stop) {
			stopInterval();
		}

		setActiveStep((prevActiveStep) => (prevActiveStep + 1 >= maxSteps ? 0 : prevActiveStep + 1));
	};

	const handleBack = () => {
		stopInterval();
		setActiveStep((prevActiveStep) => (prevActiveStep - 1 < 0 ? maxSteps - 1 : prevActiveStep - 1));
	};

	const stopInterval = () => {
		setAutomatically(false);
		clearInterval(intervalNumber);
	};

	const nextButton = () => {
		return (
			<IconButton
				aria-label="forward"
				size="small"
				onClick={() => handleNext(true)}
				className="hover:!bg-transparent "
				color="inherit"
			>
				<ArrowForwardIos className="text-[#B3B3B3] !text-base" color="inherit" fontSize="inherit" />
			</IconButton>
		);
	};

	const backButton = () => {
		return (
			<IconButton aria-label="backward" size="small" onClick={handleBack} className="hover:!bg-transparent" color="inherit">
				<ArrowBackIos className="text-[#B3B3B3] !text-base" color="inherit" fontSize="inherit" />
			</IconButton>
		);
	};

	const centerDots = () => {
		const arrayDots = Array(maxSteps > MAX_DOTS ? MAX_DOTS : maxSteps).fill(0);

		return (
			<div className="flex !ml-[-1px] mt-auto mb-auto items-center">
				{arrayDots.map((_, i) => {
					if ((i === activeStep || (activeStep > arrayDots.length - 1 && i === arrayDots.length - 1)) && automatically) {
						return (
							<div key={cards[i].title} className="relative">
								<CircularProgress
									color="inherit"
									className="text-[#D8D8D8] absolute top-[7px] mx-1"
									size={SIZE_SPINNER}
									variant="determinate"
									value={100}
									thickness={7}
								/>

								<CircularProgress
									color="inherit"
									className="text-[#B3B3B3] mx-1"
									size={SIZE_SPINNER}
									variant="determinate"
									value={progress}
									thickness={7}
								/>
							</div>
						);
					}

					return (
						<div
							key={cards[i].title}
							className={clsx({
								"w-[6px] h-[6px] rounded mx-2": true,
								"!w-[10px] !h-[10px] !rounded-lg !mx-1": activeStep === i,
								"bg-[#E5E5E5]": lightDot(i, arrayDots.length),
								"bg-[#B3B3B3]": true,
							})}
						></div>
					);
				})}
			</div>
		);
	};

	const lightDot = (i: number, total: number) => {
		if (!automatically) {
			if (activeStep >= total && i === total - 1) {
				return false;
			}
			if (i !== activeStep) {
				return true;
			}
			return false;
		}
		return true;
	};

	return (
		<>
			<Divider className="!mb-4" />

			<Box className="mb-[-16px]">
				<SwipeableViews index={activeStep}>
					{cards.map((item: Card) => {
						return (
							<Card key={item.id} elevation={0} className="!rounded-[10px] !bg-[var(--gray-background)]">
								<CardContent className="!h-[219px] !flex !pt-[20px] !pr-[14px] !pb-[18px] !pl-[14px] !text-center !items-center !justify-center !flex-col !cursor-auto ">
									<Box>
										{item.icon}
										<Typography
											component="p"
											className="!text-[13.5px] !font-semibold !text-[var(--text-color)] !mt-[0.3rem] !mb-[0.3rem] !ml-auto !mr-auto !w-[120px] !leading-[1] !font-['Montserrat']"
										>
											{item.title}
										</Typography>

										<Typography
											className="!mt-[11px] !mb-[9px] !text-[var(--text-color)] !text-sm/[13px] text-justify !leading-[1.2] font-medium break-words !font-['PTSansProNarrow'] inline-flex"
											component="p"
										>
											{item.description}
										</Typography>
									</Box>
								</CardContent>
							</Card>
						);
					})}
				</SwipeableViews>
				<div className="flex text-center justify-center my-[4px]">
					{backButton()}
					{centerDots()}
					{nextButton()}
				</div>
			</Box>
		</>
	);
};

export default TipsAndTricks;
